import React, { useEffect, useRef, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { useStoreByName } from '@web/hooks/use-stores.hooks';
import { useTranslation } from 'react-i18next';
import { Button } from '@mui/material';
import MicIndicator from '../speech/MicIndicator';
import ActionButtons from '../speech/ActionButtons';
import StartCover from './StartCover';
import TextElement from '../text/TextElement';
import { TextDisplayMode } from '@shared/types';

interface ReadingModeTextAreaProps {
  mode: Extract<TextDisplayMode, 'reading' | 'byHeart'>;
  onScoreCalculated: (scoreData: {
    score: number;
    executionTime: string;
    wordsPerMinute: number;
    time: number;
  }) => void;
}

/**
 * קומפוננטה חכמה לניהול מלא של תהליך הקריאה.
 * מנהלת את כל לוגיקת הקריאה כולל:
 * - הצגת טקסט עם סטטוס לכל מילה (נכון/שגוי/בהמתנה)
 * - ניהול טיימר וחישוב זמן קריאה
 * - שליטה על הקראה ומחיקת מילים
 * - חישוב ושמירת ציונים
 * - מניעת העתקה/הדבקה
 *
 * @param mode - מצב תצוגה ('reading' או 'byHeart')
 * @param onScoreCalculated - callback שמקבל את נתוני הציון המחושבים
 */
const ReadingModeTextArea: React.FC<ReadingModeTextAreaProps> = ({ mode, onScoreCalculated }) => {
  const { t } = useTranslation();
  const speechRecognitionStore = useStoreByName('speechRecognitionStore') as any;
  const {
    isDone,
    isStarted,
    isPaused,
    isListening,
    startListening,
    doneListening,
    currentSpokenText,
    isActuallyListening,
    score,
    clearData,
  } = speechRecognitionStore;
  
  const readingSessionStore = useStoreByName('readingSessionStore') as any;
  const { selectedBook, selectedTextEntity } = useStoreByName('booksStore');
  const { isDoneListeningDialogOpen, setIsDoneListeningDialogOpen } = useStoreByName('uiStore');
  const { saveUserScore } = useStoreByName('userStore');

  const containerRef = useRef<HTMLDivElement>(null);
  const [timerActive, setTimerActive] = useState<boolean>(false);
  const [time, setTime] = useState<number>(0);
  const [scoreCalculated, setScoreCalculated] = useState<boolean>(false);

  // לוגיקת סיום הקריאה
  const endButtonHandler = () => {
    if (isListening) {
      doneListening();
    }
  };

  // אתחול הקומפוננטה - התחלת קריאה ואיפוס state
  useEffect(() => {
    // סגירת מודל קודם אם פתוח ואיפוס ציון מחושב
    if (isDoneListeningDialogOpen) {
      setIsDoneListeningDialogOpen(false);
    }
    setScoreCalculated(false);

    // הפעלת הקריאה עם delay קצר
    const timeout = setTimeout(() => {
      startListening(true);
    }, 200);

    // ניקוי בסגירת הקומפוננטה
    return () => {
      clearTimeout(timeout);
      setTimerActive(false);
      clearData();
    };
  }, []); // רק פעם אחת באתחול!

  // איפוס הטיימר כשמתחיל להקליט
  useEffect(() => {
    if (isListening && !isPaused) {
      setTime(0);
      setTimerActive(true);
    }
  }, [isListening, isPaused]);

  // ניהול הטיימר (מופעל רק כשההקלטה פעילה)
  useEffect(() => {
    let interval: NodeJS.Timeout | undefined;
    if (timerActive && !isPaused && isListening) {
      interval = setInterval(() => {
        setTime((prevTime) => prevTime + 1);
      }, 1000);
    } else if (interval) {
      clearInterval(interval);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [timerActive, isPaused, isListening]);

  // האזנה ללחיצות מקלדת למחיקת מילה אחרונה (Backspace/Delete)
  useEffect(() => {
    const readingSession = readingSessionStore?.readingSession;
    
    // בדיקה אם יש מילים שכבר נקראו - כלומר יש מה למחוק
    const hasWordsToDelete = 
      currentSpokenText || // יש טקסט שנאמר
      (readingSession && readingSession.currentWordIndex > 0); // יש מילים שכבר נקראו (לא במילה הראשונה)
    
    const handleKeyPress = (event: KeyboardEvent) => {
      // בדיקה אם זה Backspace או Delete
      if (event.key === 'Backspace' || event.key === 'Delete') {
        // בדיקה אם יש מה למחוק והקריאה פעילה
        if (isStarted && !isDone && hasWordsToDelete) {
          // מניעת התנהגות דיפולטיבית (למשל מחיקת תווים בשדה קלט)
          event.preventDefault();
          event.stopPropagation();
          
          // שימוש ישיר בפונקציה שתיקנו במקום הלוגיקה הישנה
          speechRecognitionStore.removeLastWord();
        }
      }
    };
    
    document.addEventListener('keydown', handleKeyPress, true); // true = capture phase
    return () => {
      document.removeEventListener('keydown', handleKeyPress, true);
    };
  }, [isStarted, isDone, currentSpokenText, speechRecognitionStore, readingSessionStore]);

  // חישוב ושמירת ציון כשהדיאלוג נפתח
  useEffect(() => {
    if (!isDoneListeningDialogOpen) {
      // איפוס כשהמודל נסגר
      if (scoreCalculated) {
        setScoreCalculated(false);
      }
      return;
    }

    // חישוב וחסירת ציון רק אם טרם חושב
    if (score !== undefined && !scoreCalculated) {
      const wordsActuallyRead = currentSpokenText.split(' ').filter(Boolean).length;
      const wordsPerMinute = time > 0 ? Math.round((wordsActuallyRead / time) * 60) : 0;
      const executionTime = new Date(time * 1000).toISOString().slice(14, 19);

      const scoreData = {
        score,
        executionTime,
        wordsPerMinute,
        time,
      };

      saveUserScore({
        score,
        isByHeart: mode === 'byHeart',
        executionTime,
        wordsPerMinute,
      });

      onScoreCalculated(scoreData);
      setScoreCalculated(true);
    }
  }, [isDoneListeningDialogOpen, score, scoreCalculated, time, currentSpokenText, mode, saveUserScore, onScoreCalculated]);

  return (
    <>
      <div className="control-panel">
        <ActionButtons />
      </div>
      {/* מציגים את הטקסט עם שמירה על מבנה HTML וסטטוס לכל מילה */}
      <div className="start-cover-host">
        <TextElement
          mode={mode}
          fontClassName={
            selectedBook?.name_en?.includes('Siddur') ? 'Drogowskaz' :
            selectedBook?.name_en === 'Mishna' ? 'OpenFrank' : 'Ezra'
          }
          preventCopyPaste={true}
          containerRef={containerRef as React.RefObject<HTMLDivElement>}
        />
        <StartCover />
      </div>
      <div className="recording-mark">
        <div className={`timer ${isListening ? 'active' : ''}`}>{isListening ? new Date(time * 1000).toISOString().slice(14, 19) : ''}</div>
        <MicIndicator />
      </div>
      <Button className="circle button-end" disabled={isDone || !isStarted || isPaused} onClick={endButtonHandler}>
        {t('done')}
      </Button>
    </>
  );
};

export default observer(ReadingModeTextArea);
